/**
 * @fileoverview Month-view day cell.
 *
 * Adapted: clicking the day number switches the calendar to the day
 * view via context (no Next.js router); empty space inside the cell
 * fires `onDateCellClick` so the page can open its "create" panel.
 * The "+N more..." overflow indicator opens a Popover that lists
 * every event for the day so nothing is hidden behind a truncation.
 */

import { useMemo } from "react";
import { format, isToday, startOfDay } from "date-fns";

import { useCalendar } from "@/calendar/contexts/use-calendar";

import { EventBullet } from "@/calendar/components/month-view/event-bullet";
import { MonthEventBadge } from "@/calendar/components/month-view/month-event-badge";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";
import { getMonthCellEvents } from "@/calendar/helpers";
import { CATEGORY_META } from "@/calendar/categoryMeta";

import type { ICalendarCell, IEvent } from "@/calendar/interfaces";

interface IProps {
  cell: ICalendarCell;
  events: IEvent[];
  eventPositions: Record<string, number>;
}

const MAX_VISIBLE_EVENTS = 3;

export function DayCell({ cell, events, eventPositions }: IProps) {
  const { setView, setSelectedDate, onDateCellClick } = useCalendar();
  const { day, currentMonth, date } = cell;

  const cellEvents = useMemo(
    () => getMonthCellEvents(date, events, eventPositions),
    [date, events, eventPositions],
  );
  const isSunday = date.getDay() === 0;

  const handleDayNumberClick = () => {
    setSelectedDate(date);
    setView("day");
  };

  const handleEmptyAreaClick = () => {
    setSelectedDate(date);
    onDateCellClick(date);
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-1 border-l border-t py-1.5 lg:pb-2 lg:pt-1",
        isSunday && "border-l-0",
      )}
    >
      <button
        type="button"
        onClick={handleDayNumberClick}
        className={cn(
          "flex size-6 translate-x-1 items-center justify-center rounded-full text-xs font-semibold hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring lg:px-2",
          !currentMonth && "opacity-20",
          isToday(date) &&
            "bg-primary font-bold text-primary-foreground hover:bg-primary",
        )}
      >
        {day}
      </button>

      <div
        className={cn(
          "flex h-6 gap-1 px-2 lg:h-auto lg:min-h-[94px] lg:flex-col lg:gap-1 lg:px-0",
          !currentMonth && "opacity-50",
        )}
        onClick={(e) => {
          // Only open the create panel if the click landed on empty space,
          // not on a child badge (badges handle their own click).
          if (e.target === e.currentTarget) handleEmptyAreaClick();
        }}
      >
        {[0, 1, 2].map((position) => {
          const event = cellEvents.find((e) => e.position === position);
          const eventKey = event
            ? `event-${event.id}-${position}`
            : `empty-${position}`;

          return (
            <div key={eventKey}>
              {event && (
                <>
                  <EventBullet className="lg:hidden" color={event.color} />
                  <MonthEventBadge
                    className="hidden lg:flex"
                    event={event}
                    cellDate={startOfDay(date)}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>

      {cellEvents.length > MAX_VISIBLE_EVENTS && (
        <MoreEventsPopover
          date={date}
          extraEvents={cellEvents.slice(MAX_VISIBLE_EVENTS)}
          dimmed={!currentMonth}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// "+N more" popover — clickable overflow that lists every hidden event
// ---------------------------------------------------------------------------

function MoreEventsPopover({
  date,
  extraEvents,
  dimmed,
}: {
  date: Date;
  extraEvents: IEvent[];
  dimmed: boolean;
}) {
  const { onEventClick } = useCalendar();
  const dayLabel = format(date, "EEEE, d MMMM yyyy");

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "ml-1 h-5 self-start rounded px-1 text-left text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            dimmed && "opacity-50",
          )}
          onClick={(event) => event.stopPropagation()}
        >
          <span className="sm:hidden">+{extraEvents.length}</span>
          <span className="hidden sm:inline">+{extraEvents.length} more</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-72 p-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-semibold">{dayLabel}</p>
        </div>
        <ul className="max-h-72 space-y-1.5 overflow-y-auto">
          {extraEvents.map((event) => (
            <li key={event.id}>
              <button
                type="button"
                onClick={() => onEventClick(event)}
                className="w-full"
              >
                <MoreEventsRow event={event} />
              </button>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function MoreEventsRow({ event }: { event: IEvent }) {
  const time = !event.meta?.allDay
    ? format(new Date(event.startDate), "h:mm a")
    : "All day";
  const categoryKey = event.meta?.category ?? "custom";
  const meta = CATEGORY_META[categoryKey];
  const CategoryIcon = meta.icon;

  return (
    <div className="flex items-center gap-2 rounded-md border border-transparent px-2 py-1.5 text-left text-xs hover:bg-accent">
      <span
        className={cn(
          "inline-flex size-6 shrink-0 items-center justify-center rounded",
          meta.iconBgClass,
        )}
        title={meta.label}
      >
        <CategoryIcon className={cn("size-3", meta.iconFgClass)} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{event.title}</p>
        {event.meta?.jobName && (
          <p className="truncate text-[10px] text-muted-foreground">
            {event.meta.jobName}
          </p>
        )}
      </div>
      <span className="shrink-0 text-[10px] text-muted-foreground">{time}</span>
    </div>
  );
}
